import { DotLegalAlert, DotLegalFullScreenSpinner, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { Box } from "@mui/material";
import TrialUsageWarning from "@common/components/TrialUsageWarning";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";
import TemplateStepFields from "../../entityTemplates/templateStepFields/TemplateStepFields";
import { useAddIncidentLogPane } from "./AddIncidentLogPane.hooks";
import { AddIncidentLogPaneProps } from "./AddIncidentLogPane.types";
import IncidentLogTemplateField from "./IncidentLogTemplateField";
import EntityTemplateOption from "../../entityTemplates/EntityTemplateOption";

function AddIncidentLogPane(props: AddIncidentLogPaneProps) {
    const { translateString } = useTranslation();
    const {
        saveModel,
        modelUpdater,
        onSaveClick,
        templates,
        templatesIsLoading,
        lockedTemplateTexts,
        isLoadingTemplate,
        selectedTemplateMissingAccess,
        fieldGroups,
        editedIncidentLog,
        selectedTagIds,
        requiredFieldValidationErrors,
        validation,
        isSaving,
        showTrialLimitWarning,
    } = useAddIncidentLogPane(props);

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={translateString("incidentDialogCreateHeader")}
            okButtonText={translateString("create")}
            onOkClick={onSaveClick}
            inProgress={isSaving}
            okButtonDisabled={selectedTemplateMissingAccess}
        >
            <DotLegalSelect
                options={templates}
                onChange={modelUpdater.onTemplateChange}
                label={translateString("incidentLogTemplate")}
                noOptionsLabel={translateString("noOptions")}
                isLoading={templatesIsLoading}
                toolTipText={translateString("addIncidentLogTemplateTooltip")}
                disableClearable
                selectedItem={saveModel.entityTemplateId === "" ? null : saveModel.entityTemplateId}
                errorText={validation.getErrorsForDisplay("entityTemplateId")}
                renderOptions={(option) => <EntityTemplateOption name={option.name} lockedText={lockedTemplateTexts.get(option.id)} />}
            />

            {selectedTemplateMissingAccess && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                </Box>
            )}

            {isLoadingTemplate && <DotLegalFullScreenSpinner />}

            {!selectedTemplateMissingAccess && (
                <TemplateStepFields
                    groups={fieldGroups}
                    renderField={(field) => (
                        <IncidentLogTemplateField
                            field={field}
                            name={saveModel.name}
                            onNameChange={modelUpdater.onNameChange}
                            nameErrorText={validation.getErrorsForDisplay("name")}
                            incidentLog={editedIncidentLog}
                            onChange={modelUpdater.onTemplateFieldChange}
                            validationErrors={requiredFieldValidationErrors}
                            selectedTagIds={selectedTagIds}
                            onTagsChange={modelUpdater.onTagsChange}
                        />
                    )}
                />
            )}

            {showTrialLimitWarning && <TrialUsageWarning textKey={"createExceedingNumberOfIncidentWarning"} marginTop={2} />}
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: AddIncidentLogPane });
